import { useRef, useState, type ChangeEvent } from 'react';
import { AlertTriangle, ExternalLink, Loader2, RefreshCw, Trash2, Upload } from 'lucide-react';
import { ccaaLabel } from './ccaa';
import { openLicenseLeaflet } from './licenseLeafletFile';
import {
  useLicenseLeafletsAdmin,
  type LicenseLeafletOrphan,
  type LicenseLeafletRow,
  type LicenseLeafletUse,
} from './hooks/useLicenseLeaflets';

const SMALL_LEAFLET_BYTES = 400 * 1024;

const SCOPE_LABEL: Record<LicenseLeafletUse['scope'], string> = {
  continental: 'continental',
  maritima: 'marítima',
  submarina: 'submarina',
};

const TERRITORY_LABEL: Record<string, string> = {
  'comunitat-valenciana': 'Comunitat Valenciana',
};

function territoryLabel(code: string): string {
  return TERRITORY_LABEL[code] || ccaaLabel(code);
}

function formatBytes(bytes: number | null): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb).toLocaleString('es-ES')} KB`;
  return `${(kb / 1024).toLocaleString('es-ES', { maximumFractionDigits: 1 })} MB`;
}

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
}

function useLabel(use: LicenseLeafletUse): string {
  return `${territoryLabel(use.territory)} · ${SCOPE_LABEL[use.scope]}`;
}

export function LicenseLeafletsPanel() {
  const { list, replace, remove } = useLicenseLeafletsAdmin();
  const inputRef = useRef<HTMLInputElement>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [opening, setOpening] = useState<string | null>(null);

  const leaflets = list.data?.leaflets ?? [];
  const orphans = list.data?.orphans ?? [];
  const presentCount = leaflets.filter((row) => row.present).length;
  const missingCount = leaflets.length - presentCount;
  const busyName = replace.isPending
    ? replace.variables?.blobName
    : remove.isPending
      ? remove.variables
      : opening;

  function startReplace(blobName: string) {
    const input = inputRef.current;
    if (!input) return;
    input.dataset.blobName = blobName;
    input.value = '';
    input.click();
  }

  async function onFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    const blobName = event.target.dataset.blobName || '';
    event.target.value = '';
    if (!file || !blobName) return;
    setNotice(null);
    setActionError(null);
    try {
      await replace.mutateAsync({ blobName, file });
      setNotice('Folleto sustituido. El siguiente correo de entrega adjuntará este PDF.');
    } catch (error: unknown) {
      setActionError(error instanceof Error ? error.message : 'No se ha podido sustituir el folleto');
    }
  }

  async function onOpen(blobName: string) {
    setActionError(null);
    setOpening(blobName);
    try {
      await openLicenseLeaflet(blobName);
    } catch (error: unknown) {
      setActionError(error instanceof Error ? error.message : 'No se ha podido abrir el folleto');
    } finally {
      setOpening(null);
    }
  }

  async function onDelete(row: LicenseLeafletRow) {
    const where = row.uses.map(useLabel).join(', ');
    const confirmed = window.confirm(
      `¿Eliminar el folleto de ${where} del Blob? El siguiente correo de ese ámbito saldrá sin adjunto.`
    );
    if (!confirmed) return;
    setNotice(null);
    setActionError(null);
    try {
      await remove.mutateAsync(row.blobName);
      setNotice('Folleto eliminado del Blob.');
    } catch (error: unknown) {
      setActionError(error instanceof Error ? error.message : 'No se ha podido eliminar el folleto');
    }
  }

  async function onDeleteOrphan(orphan: LicenseLeafletOrphan) {
    const confirmed = window.confirm(
      `¿Eliminar ${orphan.name} del Blob? El correo de entrega no lo usa.`
    );
    if (!confirmed) return;
    setNotice(null);
    setActionError(null);
    try {
      await remove.mutateAsync(orphan.name);
      setNotice('Fichero eliminado del Blob.');
    } catch (error: unknown) {
      setActionError(error instanceof Error ? error.message : 'No se ha podido eliminar el fichero');
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-muted-foreground max-w-3xl">
          Cada licencia adjunta el PDF de su comunidad y ámbito, con el nombre de esta tabla.
          Sustituir guarda el fichero nuevo encima de ese nombre: el siguiente correo lo lleva.
          Los correos ya enviados no cambian.
        </p>
        <button
          type="button"
          onClick={() => void list.refetch()}
          disabled={list.isFetching}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground bg-muted/50 shrink-0"
        >
          <RefreshCw size={14} className={list.isFetching ? 'animate-spin' : ''} />
          Actualizar
        </button>
      </div>

      {list.data && (
        <p className="text-sm">
          {leaflets.length} folletos · {presentCount} en el Blob
          {missingCount > 0 ? ` · ${missingCount} sin fichero` : ''}
          {orphans.length > 0 ? ` · ${orphans.length} con otro nombre` : ''}
        </p>
      )}

      {notice && <p className="text-sm text-emerald-500">{notice}</p>}
      {actionError && <p className="text-sm text-rose-500">{actionError}</p>}
      {list.error && (
        <p className="text-sm text-rose-500">
          {list.error instanceof Error ? list.error.message : 'No se ha podido cargar el inventario'}
        </p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="sr-only"
        onChange={(event) => void onFile(event)}
      />

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">Dónde se usa</th>
              <th className="px-3 py-2 font-medium">Nombre en el Blob</th>
              <th className="px-3 py-2 font-medium">Tamaño</th>
              <th className="px-3 py-2 font-medium">Actualizado</th>
              <th className="px-3 py-2 font-medium">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-muted-foreground">
                  <Loader2 className="inline animate-spin mr-2" size={14} />
                  Cargando folletos…
                </td>
              </tr>
            )}
            {leaflets.map((row) => {
              const small = row.present && (row.contentLength ?? 0) > 0 && (row.contentLength ?? 0) < SMALL_LEAFLET_BYTES;
              const busy = busyName === row.blobName;
              return (
                <tr key={row.blobName} className="border-t border-border align-top">
                  <td className="px-3 py-2">
                    {row.uses.map((use) => (
                      <div key={`${use.territory}-${use.scope}`}>{useLabel(use)}</div>
                    ))}
                    {row.note && <div className="text-xs text-muted-foreground mt-1">{row.note}</div>}
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-mono text-xs break-all">{row.blobName}</div>
                    {!row.present && <div className="text-xs text-amber-500 mt-1">No está en el Blob</div>}
                    {row.present && row.contentLength === 0 && (
                      <div className="text-xs text-amber-500 mt-1">El fichero está vacío</div>
                    )}
                    {small && (
                      <div className="text-xs text-amber-500 mt-1 flex items-center gap-1">
                        <AlertTriangle size={12} />
                        Peso bajo: ábrelo y comprueba que es el folleto bueno
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{formatBytes(row.contentLength)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{formatWhen(row.lastModified)}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      <button
                        type="button"
                        disabled={!row.present || busy}
                        onClick={() => void onOpen(row.blobName)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border text-xs disabled:opacity-40"
                      >
                        <ExternalLink size={12} /> Abrir
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => startReplace(row.blobName)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border text-xs disabled:opacity-40"
                      >
                        {busy && replace.isPending ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                        Sustituir
                      </button>
                      <button
                        type="button"
                        disabled={!row.present || busy}
                        onClick={() => void onDelete(row)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border text-xs text-rose-500 disabled:opacity-40"
                      >
                        <Trash2 size={12} /> Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {list.data && (
      <section className="space-y-2">
        <h2 className="text-base font-semibold">Otros ficheros en el contenedor</h2>
        <p className="text-sm text-muted-foreground">
          Nombres que el correo no busca. Si hay una copia comprimida o una versión vieja, aparece aquí.
        </p>
        {orphans.length === 0 && list.data && (
          <p className="text-sm text-muted-foreground">No hay otros ficheros.</p>
        )}
        {orphans.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">Nombre</th>
                  <th className="px-3 py-2 font-medium">Tamaño</th>
                  <th className="px-3 py-2 font-medium">Actualizado</th>
                  <th className="px-3 py-2 font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {orphans.map((orphan) => {
                  const busy = busyName === orphan.name;
                  return (
                    <tr key={orphan.name} className="border-t border-border align-top">
                      <td className="px-3 py-2 font-mono text-xs break-all">{orphan.name}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{formatBytes(orphan.contentLength)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{formatWhen(orphan.lastModified)}</td>
                      <td className="px-3 py-2">
                        {orphan.manageable ? (
                          <div className="flex flex-wrap gap-1">
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => void onOpen(orphan.name)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border text-xs disabled:opacity-40"
                            >
                              <ExternalLink size={12} /> Abrir
                            </button>
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => void onDeleteOrphan(orphan)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border text-xs text-rose-500 disabled:opacity-40"
                            >
                              <Trash2 size={12} /> Eliminar
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            El nombre no se puede borrar desde aquí.
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      )}
    </div>
  );
}
